import type { RetailerTrendDay } from "../../services/RetailerTrendsService";

export type TrendMode = "total" | "per";

export interface TrendMetric {
  title: string;
  // Second series drawn dashed behind the main line (stores with users).
  legend?: boolean;
  cap?: number;
  value: (d: RetailerTrendDay) => number | null;
  second?: (d: RetailerTrendDay) => number | null;
  axis: (v: number) => string;
  big: (d: RetailerTrendDay) => string;
  tip: (d: RetailerTrendDay) => string;
}

export const DROP_RATIO = 0.8; // a day 20%+ under its 7-day normal is a drop
// A chronic gap never looks like a drop against its own normal, so coverage
// under this is called out on its own.
export const LOW_COVERAGE_PCT = 80;

export const compact = (n: number) => {
  if (n >= 1e6) return `${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1)}M`;
  if (n >= 1e4) return `${Math.round(n / 1e3)}k`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
};
export const full = (n: number) => Math.round(n).toLocaleString("en-US");

const ratio = (a: number, b: number) => (b > 0 ? a / b : null);
const avail = (d: RetailerTrendDay, v: number | null) => (d.available ? v : null);
const coverage = (d: RetailerTrendDay) => avail(d, d.storesWithUsers > 0 ? (d.storesCrawled / d.storesWithUsers) * 100 : null);
const perStore = (d: RetailerTrendDay) => avail(d, ratio(d.products, d.storesCrawled));
const usersPerStore = (d: RetailerTrendDay) => avail(d, ratio(d.users, d.storesWithUsers));

export const METRICS: Record<TrendMode, TrendMetric[]> = {
  total: [
    {
      title: "Users with stores",
      value: (d) => avail(d, d.users),
      axis: compact,
      big: (d) => full(d.users),
      tip: (d) => `${full(d.users)} users`,
    },
    {
      title: "Stores crawled",
      legend: true,
      value: (d) => avail(d, d.storesCrawled),
      second: (d) => avail(d, d.storesWithUsers),
      axis: compact,
      big: (d) => full(d.storesCrawled),
      tip: (d) => `${full(d.storesCrawled)} of ${full(d.storesWithUsers)} (${Math.round(coverage(d) ?? 0)}%)`,
    },
    {
      title: "Products uploaded",
      value: (d) => avail(d, d.products),
      axis: compact,
      big: (d) => full(d.products),
      tip: (d) => `${full(d.products)} products`,
    },
  ],
  per: [
    {
      title: "Users per store",
      value: usersPerStore,
      axis: (v) => v.toFixed(2),
      big: (d) => usersPerStore(d)?.toFixed(2) ?? "—",
      tip: (d) => `${usersPerStore(d)?.toFixed(2) ?? "—"} users per store`,
    },
    {
      title: "Store coverage",
      cap: 100,
      value: coverage,
      axis: (v) => `${Math.round(v)}%`,
      big: (d) => `${(coverage(d) ?? 0).toFixed(1)}%`,
      tip: (d) => `${(coverage(d) ?? 0).toFixed(1)}% · ${full(d.storesCrawled)} of ${full(d.storesWithUsers)}`,
    },
    {
      title: "Products per crawled store",
      value: perStore,
      axis: compact,
      big: (d) => (perStore(d) == null ? "—" : full(perStore(d)!)),
      tip: (d) => (perStore(d) == null ? "No stores crawled" : `${full(perStore(d)!)} per store`),
    },
  ],
};

/** Median of the (up to) 7 available values before index i. */
export function baselineAt(values: (number | null)[], i: number): number | null {
  const prior: number[] = [];
  for (let k = i - 1; k >= 0 && prior.length < 7; k--) {
    const v = values[k];
    if (v != null) prior.push(v);
  }
  if (prior.length < 3) return null;
  prior.sort((a, b) => a - b);
  return prior[Math.floor(prior.length / 2)];
}

export function isDrop(value: number | null, base: number | null): boolean {
  return value != null && base != null && base > 0 && value < base * DROP_RATIO;
}

export function deltaLabel(value: number | null, base: number | null): string {
  if (value == null || base == null || base <= 0) return "";
  const d = Math.round(((value - base) / base) * 100);
  return `${d > 0 ? "+" : d < 0 ? "−" : ""}${Math.abs(d)}% vs 7-day normal`;
}

function niceStep(span: number) {
  const raw = span / 3;
  const p = Math.pow(10, Math.floor(Math.log10(raw)));
  const m = raw / p;
  return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10) * p;
}

export function niceScale(min: number, max: number, cap?: number) {
  let span = max - min;
  if (span <= 0) span = Math.abs(max) || 1;
  const step = niceStep(span);
  const lo = Math.max(0, Math.floor(min / step) * step);
  let hi = Math.ceil(max / step) * step;
  if (cap != null && hi > cap) hi = cap;
  if (hi <= lo) hi = lo + step;
  const ticks: number[] = [];
  for (let v = lo; v <= hi + step * 1e-6; v += step) ticks.push(Math.round(v * 1e6) / 1e6);
  return { lo, hi, ticks };
}

/** SVG path through the values; a null breaks the line so missing days show as gaps. */
export function linePath(values: (number | null)[], lo: number, hi: number, w: number, h: number): string {
  const n = values.length;
  let out = "";
  let pen = false;
  values.forEach((v, i) => {
    if (v == null) {
      pen = false;
      return;
    }
    const x = n === 1 ? 0 : (i / (n - 1)) * w;
    const y = h - ((v - lo) / (hi - lo)) * h;
    out += `${pen ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)} `;
    pen = true;
  });
  return out.trim();
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function dayLabel(date: string): string {
  const [, m, d] = date.split("-").map(Number);
  return `${MONTHS[m - 1]} ${d}`;
}

/** Index of the last day in [from, days.length) that has a snapshot, or -1. */
export function latestAvailable(days: RetailerTrendDay[], from = 0): number {
  for (let i = days.length - 1; i >= from; i--) if (days[i].available) return i;
  return -1;
}

/** The first metric (totals, then per-store) that dropped on day i, or null. */
export function dropOn(days: RetailerTrendDay[], i: number): string | null {
  if (i < 0) return null;
  for (const metric of [...METRICS.total, ...METRICS.per]) {
    const values = days.map(metric.value);
    if (isDrop(values[i], baselineAt(values, i))) return metric.title;
  }
  return null;
}

/** Store coverage % on day i when it is under LOW_COVERAGE_PCT, else null. */
export function lowCoverageOn(days: RetailerTrendDay[], i: number): number | null {
  if (i < 0) return null;
  const pct = METRICS.per[1].value(days[i]);
  return pct != null && pct < LOW_COVERAGE_PCT ? pct : null;
}
